import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getStudents } from '../utils/api';
import StudentTable from '../components/StudentTable';
import { Users, UserPlus, ClipboardCheck, Search, TrendingUp } from 'lucide-react';

export default function StudentListPage() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await getStudents();
        if (mounted) setStudents(res.students || []);
      } catch (err) {} finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Filter students based on search query
  const filteredStudents = students.filter(student =>
    student.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    student.class?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    student.rollNo?.toString().includes(searchQuery)
  );

  // Calculate stats
  const totalStudents = students.length;
  // Helper to get normalized status from possible API shapes
  const getStatus = (s) => {
    // Common locations: s.status, s.lastAttendance?.status, s.latestAttendance?.status
    const raw = s?.status || s?.lastAttendance?.status || s?.latestAttendance?.status || s?.attendanceStatus || '';
    if (!raw) return '';
    return String(raw).trim().toLowerCase();
  };

  const presentToday = students.filter(s => {
    const st = getStatus(s);
    return st === 'present' || st.startsWith('present');
  }).length;

  const absentToday = students.filter(s => {
    const st = getStatus(s);
    return st === 'absent' || st.startsWith('absent');
  }).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header Section */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Class 6-A: Student Attendance Sheet</h1>
              <p className="text-gray-600">Manage and track student attendance</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-2 font-medium"
                onClick={() => navigate('/registerstudent')}
              >
                <UserPlus size={18} />
                Add Student
              </button>
              <button
                className="px-5 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-2 font-medium"
                onClick={() => navigate('/takeattendance')}
              >
                <ClipboardCheck size={18} />
                Take Attendance
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Total Students</p>
                <p className="text-3xl font-bold text-gray-900">{totalStudents}</p>
                <div className="flex items-center gap-1 mt-2">
                  <TrendingUp size={14} className="text-green-500" />
                  <span className="text-xs text-green-600 font-medium">Active</span>
                </div>
              </div>
              <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                <Users className="text-white" size={28} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Present Today</p>
                <p className="text-3xl font-bold text-gray-900">{presentToday}</p>
                <div className="flex items-center gap-1 mt-2">
                  <span className="text-xs text-gray-500 font-medium">
                    {totalStudents > 0 ? Math.round((presentToday / totalStudents) * 100) : 0}% attendance
                  </span>
                </div>
              </div>
              <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
                <ClipboardCheck className="text-white" size={28} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Absent Today</p>
                <p className="text-3xl font-bold text-gray-900">{absentToday}</p>
                <div className="flex items-center gap-1 mt-2">
                  <span className="text-xs text-gray-500 font-medium">
                    {totalStudents > 0 ? Math.round((absentToday / totalStudents) * 100) : 0}% absent
                  </span>
                </div>
              </div>
              <div className="w-14 h-14 bg-gradient-to-br from-red-500 to-red-600 rounded-xl flex items-center justify-center shadow-lg">
                <Users className="text-white" size={28} />
              </div>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        {!loading && students.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by name, class, or roll number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>
          </div>
        )}

        {/* Content Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mb-4"></div>
              <p className="text-gray-600 font-medium">Loading students...</p>
            </div>
          ) : students.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <Users className="text-gray-400" size={40} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Students Yet</h3>
              <p className="text-gray-600 mb-6 text-center">Get started by adding your first student to the system.</p>
              <button
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 font-medium"
                onClick={() => navigate('/registerstudent')}
              >
                <UserPlus size={18} />
                Add First Student
              </button>
            </div>
          ) : filteredStudents.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <Search className="text-gray-400" size={40} />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Results Found</h3>
              <p className="text-gray-600 text-center">No students match your search criteria. Try a different search term.</p>
            </div>
          ) : (
            <StudentTable students={filteredStudents} />
          )}
        </div>

        {/* Footer Info */}
        {!loading && students.length > 0 && (
          <div className="text-center text-sm text-gray-500">
            Showing {filteredStudents.length} of {totalStudents} student{totalStudents !== 1 ? 's' : ''}
          </div>
        )}
      </div>
    </div>
  );
}